import LogoPixelCache from 'app/data/HomePixelCache/LogoPixelCache';

import type { Vector2 } from 'app/contracts/Geometry';

/**
 * Experimental icon-density knob (`?iconCount=N`) — a DEV / exploratory override
 * that greatly increases the intro logo's particle count to put the SoA headroom
 * the S1-GATE weak-GPU read measured to visible use. docs/PERF-shaders-scaling.md:
 * `particles-optimised` holds a locked 60 fps to ~64k systems particles on a
 * Mali-G78 phone, so a ~30k-point logo should stay smooth there too — this knob
 * is how you check that on a device.
 *
 * OFF by default: no `?iconCount` (or ≤ the baked count) → the untouched
 * 1571-point logo. It is NOT a production feature — the default is unchanged; the
 * caller (ParticlesOptimisedWebGL.create) only applies it when the point count
 * actually differs, and it must run BEFORE `new ParticleCore()` (which sizes the
 * icon pool from HomeFieldConfig) and lift `iconParticlesMobile` (IconDepartment
 * caps the mobile active count there, which would otherwise clamp the phone to
 * 1000). Clamped to MAX_ICON as a self-OOM guard for the ungated query.
 *
 * Densification: the baked cache is ~1571 "on" cells on a ~60×60 lattice
 * (step ~0.0167, normalised [0,1]). We jitter-fill each cell with
 * `perCell = round(N / base)` uniformly-random points centred on the cell, so the
 * result is ~N points forming a much denser stipple of the same logo shape.
 */

const MAX_ICON = 120000;

function iconCountOverride(): number {
	if (typeof location === 'undefined') {
		return 0;
	}
	const raw = new URLSearchParams(location.search).get('iconCount');
	if (!raw) {
		return 0;
	}
	const n = parseInt(raw, 10);
	if (!Number.isFinite(n) || n <= 0) {
		return 0;
	}
	return Math.min(n, MAX_ICON);
}

/** The logo grid step = the smallest positive gap between distinct x values. */
function gridStep(base: Array<Vector2>): number {
	const xs = [...new Set(base.map((p) => p.x))].sort((a, b) => a - b);
	let step = Infinity;
	for (let i = 1; i < xs.length; i++) {
		const d = xs[i] - xs[i - 1];
		if (d > 1e-6 && d < step) {
			step = d;
		}
	}
	return Number.isFinite(step) ? step : 0.0167;
}

/**
 * The logo point set for this page load: the baked cache unchanged, or — when
 * `?iconCount=N` asks for materially more — a densified stipple of ~N points.
 * Length is authoritative: the caller sizes the icon pool from it.
 */
export function resolveIconPixels(): Array<Vector2> {
	const base = LogoPixelCache.data as Array<Vector2>;
	const target = iconCountOverride();
	const perCell = Math.round(target / base.length);
	if (target === 0 || perCell <= 1) {
		return base;
	}

	const step = gridStep(base);
	const half = step / 2;
	const out: Array<Vector2> = new Array(base.length * perCell);
	let w = 0;
	for (let i = 0; i < base.length; i++) {
		const px = base[i].x;
		const py = base[i].y;
		for (let j = 0; j < perCell; j++) {
			out[w++] = { x: px - half + Math.random() * step, y: py - half + Math.random() * step };
		}
	}
	return out;
}
